import React, { useEffect, useRef, useState } from "react";
import {
  X,
  User,
  Mail,
  Phone,
  Wallet,
  FileText,
  Upload,
  Edit3,
  Save,
  BriefcaseBusiness,
} from "lucide-react";

const ProfileItem = ({ label, value }) => {
  return (
    <div className="rounded-lg border border-[#e7e7e4] bg-[#fafaf8] px-3.5 py-3">
      <p className="mb-1 text-[8px] font-semibold uppercase tracking-[0.08em] text-[#92989a]">
        {label}
      </p>

      <p className="truncate text-[11px] font-medium text-[#243d49]">
        {value || "—"}
      </p>
    </div>
  );
};

const ProfileInput = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = true,
  disabled = false,
  icon: Icon,
}) => {
  return (
    <div>
      <label className="mb-2 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
        {Icon && <Icon size={11} className="text-[#6a7847]" />}

        {label}

        {required && label !== "Wallet Address" && <span className="text-[#4c7a2e]">*</span>}
      </label>

      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={label === "Wallet Address" ? false : required}
        className={`
          h-[42px]
          w-full
          rounded-[10px]
          border
          px-3
          text-[11px]
          text-[#243d49]
          outline-none
          transition
          ${
            disabled
              ? "cursor-not-allowed border-[#e3e4e0] bg-[#f3f3f1] text-[#92989a]"
              : "border-[#dfe4dd] bg-white focus:border-[#7da458] focus:ring-2 focus:ring-[#7da458]/10"
          }
        `}
      />
    </div>
  );
};


const ProfileSelect = ({
  label,
  value,
  onChange,
  required = true,
  icon: Icon,
}) => {
  return (
    <div>
      <label className="mb-2 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
        {Icon && <Icon size={11} className="text-[#6a7847]" />}

        {label}

        {required && <span className="text-[#4c7a2e]">*</span>}
      </label>

      <select
        value={value}
        disabled
        onChange={onChange}
        required={required}
        className="
          h-[42px]
          w-full
          cursor-not-allowed
          rounded-[10px]
          border
          border-[#e3e4e0]
          bg-[#f3f3f1]
          px-3
          text-[11px]
          text-[#92989a]
          outline-none
        "
      >
        <option value="">Select Role</option>
        <option value="Investor">Investor</option>
        <option value="Customer">Customer</option>
      </select>
    </div>
  );
};
const ProfileModal = ({
  isOpen,
  onClose,
  profile,
  isLoading,
  onUpdateProfile,
}) => {
  const fileInputRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    wallet_address: "",
    role: "",
  });

  const [emiratesId, setEmiratesId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!profile) return;

    const firstName =
      profile?.first_name ||
      profile?.firstname ||
      profile?.firstName ||
      "";

    const lastName =
      profile?.last_name ||
      profile?.lastname ||
      profile?.lastName ||
      "";

    const email =
      profile?.email ||
      profile?.email_address ||
      "";

    const phone =
      profile?.phone ||
      profile?.phone_number ||
      profile?.mobile ||
      "";

    const walletAddress =
      profile?.wallet_address ||
      profile?.walletAddress ||
      "";

    const role =
      profile?.role ||
      profile?.user_role ||
      "Investor";

    setFormData({
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      wallet_address: walletAddress,
      role,
    });
  }, [profile]);

  if (!isOpen) return null;

  const firstName =
    profile?.first_name ||
    profile?.firstname ||
    profile?.firstName ||
    "";

  const lastName =
    profile?.last_name ||
    profile?.lastname ||
    profile?.lastName ||
    "";

  const fullName =
    profile?.name ||
    profile?.full_name ||
    profile?.fullName ||
    `${firstName} ${lastName}`.trim();

  const email =
    profile?.email ||
    profile?.email_address ||
    "";

  const phone =
    profile?.phone ||
    profile?.phone_number ||
    profile?.mobile ||
    "";

  const walletAddress =
    profile?.wallet_address ||
    profile?.walletAddress ||
    "";

  const role =
    profile?.role ||
    profile?.user_role ||
    "Investor";

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (error) {
      setError("");
    }
  };

  const handleEmiratesIdChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      setError("Please upload a PDF, JPG or PNG file.");
      e.target.value = "";
      return;
    }

    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      setError("Emirates ID file size must be less than 10MB.");
      e.target.value = "";
      return;
    }

    setEmiratesId(file);
    setError("");
  };

  const removeEmiratesId = () => {
    setEmiratesId(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleEdit = () => {
    setError("");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setError("");
    setEmiratesId(null);
    setIsEditing(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Restore original values
    setFormData({
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      wallet_address: walletAddress,
      role,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!formData.first_name.trim()) {
      setError("First name is required.");
      return;
    }

    if (!formData.last_name.trim()) {
      setError("Last name is required.");
      return;
    }

    // if (!formData.phone.trim()) {
    //   setError("Phone number is required.");
    //   return;
    // }

    // if (!formData.wallet_address.trim()) {
    //   setError("Wallet address is required.");
    //   return;
    // }

    // if (!formData.role.trim()) {
    //   setError("Role is required.");
    //   return;
    // }

    // Emirates ID is required because backend currently
    // does not return the existing Emirates ID.
    // if (!emiratesId) {
    //   setError("Please upload your Emirates ID.");
    //   return;
    // }

    try {
      setIsSaving(true);

      const payload = new FormData();

      payload.append("first_name", formData.first_name.trim());
      payload.append("last_name", formData.last_name.trim());
    //   payload.append("email", formData.email);
    if(formData.phone){
      payload.append("phone_number", formData.phone.trim());
    }
      payload.append(
        "wallet_address",
        formData.wallet_address.trim()
      );
    if(emiratesId){
      payload.append("emirates_id", emiratesId);
    }

      if (onUpdateProfile) {
        await onUpdateProfile(payload);
      }

      setIsEditing(false);
      setEmiratesId(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Profile update error:", err);

      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Unable to update profile. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };


   const handlePhoneChange = (e) => {
  let value = e.target.value;

  let digits = value.replace(/\D/g, "");

  if (digits.startsWith("971")) {
    digits = digits.slice(3);
  }

  digits = digits.slice(0, 9);

  let formatted = "+971";

  if (digits.length > 0) {
    formatted += ` ${digits.slice(0, 2)}`;
  }

  if (digits.length > 2) {
    formatted += ` ${digits.slice(2, 5)}`;
  }

  if (digits.length > 5) {
    formatted += ` ${digits.slice(5, 9)}`;
  }

  handleChange("phone", formatted);
};

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 backdrop-blur-[3px]"
      onClick={onClose}
    >
      <div
        className="
          w-full
          max-w-[560px]
          max-h-[90vh]
          overflow-hidden
          rounded-2xl
          bg-white
          shadow-2xl
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between border-b border-[#e7e7e4] px-5 py-4">
          <div>
            <h2 className="text-[17px] font-semibold text-[#183542]">
              My Profile
            </h2>

            <p className="mt-1 text-[9px] text-[#9a9e9f]">
              {isEditing
                ? "Update your account information"
                : "Your account information"}
            </p>
          </div>

          <button
            type="button"
            onClick={()=>{
                setIsEditing(false);
                setEmiratesId(null)
                onClose()
            }}
            className="
              flex
              h-8
              w-8
              items-center
              justify-center
              rounded-full
              bg-[#f3f4f2]
              text-[#6f777a]
              transition
              hover:bg-[#e7e9e7]
            "
          >
            <X size={15} />
          </button>
        </div>

        <div className="max-h-[calc(90vh-80px)] overflow-y-auto p-5">
          {isLoading ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 animate-pulse rounded-full bg-gray-200" />

                <div className="flex-1 space-y-2">
                  <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
                  <div className="h-2 w-20 animate-pulse rounded bg-gray-200" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="h-[60px] animate-pulse rounded-lg bg-gray-200" />
                <div className="h-[60px] animate-pulse rounded-lg bg-gray-200" />
                <div className="h-[60px] animate-pulse rounded-lg bg-gray-200" />
                <div className="h-[60px] animate-pulse rounded-lg bg-gray-200" />
              </div>
            </div>
          ) : profile ? (
            <>
              {/* PROFILE HEADER */}
              <div className="mb-5 flex items-center justify-between gap-3 rounded-xl bg-[#f8f8f6] p-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#e9dfca] text-[14px] font-bold text-[#20485b]">
                    {(
                      `${firstName?.[0] || ""}${
                        lastName?.[0] || ""
                      }` ||
                      fullName?.[0] ||
                      "I"
                    ).toUpperCase()}
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold text-[#1b3441]">
                      {isEditing
                        ? `${formData.first_name} ${formData.last_name}`.trim() ||
                          "Investor"
                        : fullName || "Investor"}
                    </p>

                    <p className="mt-1 text-[9px] text-[#8a9295]">
                      {role}
                    </p>
                  </div>
                </div>

                {!isEditing && (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="
                      flex
                      shrink-0
                      items-center
                      gap-1.5
                      rounded-lg
                      
                      px-3
                      py-2
                      text-[9px]
                      font-semibold
                      text-white
                      transition
                      border-[#4c7a2e]
                      bg-[#4c7a2e]
                    hover:-translate-y-px
                    hover:border-[#375c20]
                    hover:bg-[#375c20]
                    hover:shadow-[#375c20]
                    "
                  >
                    <Edit3 size={12} />
                    Edit Profile
                  </button>
                )}
              </div>

              {!isEditing ? (
                /* ================= VIEW MODE ================= */
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <ProfileItem
                      label="First Name"
                      value={firstName}
                    />

                    <ProfileItem
                      label="Last Name"
                      value={lastName}
                    />

                    <ProfileItem
                      label="Email"
                      value={email}
                    />

                    <ProfileItem
                      label="Phone"
                      value={phone}
                    />

                    <div className="sm:col-span-2">
                      <ProfileItem
                        label="Wallet Address"
                        value={walletAddress}
                      />
                    </div>

                    <ProfileItem
                      label="Role"
                      value={role}
                    />
                  </div>
                </>
              ) : (
                /* ================= EDIT MODE ================= */
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <ProfileInput
                      label="First Name"
                      icon={User}
                      value={formData.first_name}
                      placeholder="Enter first name"
                      onChange={(e) =>
                        handleChange(
                          "first_name",
                          e.target.value
                        )
                      }
                    />

                    <ProfileInput
                      label="Last Name"
                      icon={User}
                      value={formData.last_name}
                      placeholder="Enter last name"
                      onChange={(e) =>
                        handleChange(
                          "last_name",
                          e.target.value
                        )
                      }
                    />

                    <ProfileInput
                      label="Email"
                      icon={Mail}
                      value={formData.email}
                      disabled
                      required={false}
                      placeholder="Email"
                    />

                    {/* <ProfileInput
                      label="Phone"
                      icon={Phone}
                      value={formData.phone}
                      placeholder="Enter phone number"
                      onChange={(e) =>
                        handleChange(
                          "phone",
                          e.target.value
                        )
                      }
                    /> */}
                    <div>
  <label className="mb-2 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
    <Phone size={11} className="text-[#6a7847]" />

    Phone Number

    {/* <span className="text-[#4c7a2e]">*</span> */}
  </label>

  <input
    type="tel"
    value={formData.phone || ""}
    onChange={handlePhoneChange}
    placeholder="+971 50 000 0000"
    className="
      h-[42px]
      w-full
      rounded-[10px]
      border
      border-[#dfe4dd]
      bg-white
      px-3
      text-[11px]
      text-[#243d49]
      outline-none
      transition
      placeholder:text-[#a5aaa3]
      focus:border-[#7da458]
      focus:ring-2
      focus:ring-[#7da458]/10
    "
  />
</div>
                    

                    <div className="sm:col-span-2">
                      <ProfileInput
                        label="Wallet Address"
                        icon={Wallet}
                        value={formData.wallet_address}
                        placeholder="Enter wallet address"
                        onChange={(e) =>
                          handleChange(
                            "wallet_address",
                            e.target.value
                          )
                        }
                      />
                    </div>

                    <ProfileSelect
                      label="Role"
                      icon={BriefcaseBusiness}
                      value={formData.role}
                      onChange={(e) =>
                        handleChange(
                          "role",
                          e.target.value
                        )
                      }
                    />

                    {/* EMIRATES ID */}
                    <div className="sm:col-span-2">
                      <label className="mb-2 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#77818c]">
                        <FileText
                          size={11}
                          className="text-[#6a7847]"
                        />

                        Emirates ID

                        {/* <span className="text-[#4c7a2e]">
                          *
                        </span> */}
                      </label>

                      {!emiratesId ? (
                        <button
                          type="button"
                          onClick={() =>
                            fileInputRef.current?.click()
                          }
                          className="
                            flex
                            min-h-[92px]
                            w-full
                            flex-col
                            items-center
                            justify-center
                            rounded-[12px]
                            border
                            border-dashed
                            border-[#cfd9c8]
                            bg-[#fafcf8]
                            px-4
                            text-center
                            transition
                            hover:border-[#7da458]
                            hover:bg-[#f6faf2]
                          "
                        >
                          <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-[#eaf2e3] text-[#4c7a2e]">
                            <Upload size={14} />
                          </div>

                          <p className="text-[10px] font-semibold text-[#596257]">
                            Upload Emirates ID
                          </p>

                          <p className="mt-1 text-[8px] text-[#9a9f96]">
                            PDF, JPG or PNG · Max 10MB
                          </p>
                        </button>
                      ) : (
                        <div className="flex items-center justify-between rounded-[12px] border border-[#dce7d5] bg-[#f7faf4] px-3.5 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e6f0df] text-[#4c7a2e]">
                              <FileText size={15} />
                            </div>

                            <div className="min-w-0">
                              <p className="truncate text-[10px] font-semibold text-[#37452f]">
                                {emiratesId.name}
                              </p>

                              <p className="mt-0.5 text-[8px] text-[#92968e]">
                                {(
                                  emiratesId.size /
                                  (1024 * 1024)
                                ).toFixed(2)}{" "}
                                MB
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={removeEmiratesId}
                            className="
                              ml-3
                              flex
                              h-7
                              w-7
                              shrink-0
                              items-center
                              justify-center
                              rounded-full
                              bg-white
                              text-[#8c9388]
                              shadow-sm
                              transition
                              hover:text-[#b45850]
                            "
                          >
                            <X size={13} />
                          </button>
                        </div>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={handleEmiratesIdChange}
                      />
                    </div>
                  </div>

                  {/* ERROR */}
                  {error && (
                    <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                      <p className="text-[9px] font-medium text-red-600">
                        {error}
                      </p>
                    </div>
                  )}

                  {/* ACTIONS */}
                  {/* ACTIONS */}
                    <div className="mt-6 flex items-center justify-end gap-3 border-t border-[#ebe9e2] pt-5">
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="
                          inline-flex
                          h-[40px]
                          items-center
                          justify-center
                          rounded-[10px]
                          border
                          border-[#d9ddd8]
                          bg-[#f8f8f5]
                          px-5
                          text-[10px]
                          font-semibold
                          tracking-[0.02em]
                          text-[#59656a]
                          transition-all
                          duration-200
                          hover:border-[#c9cec8]
                          hover:bg-[#f1f2ee]
                          hover:text-[#31464f]
                          disabled:cursor-not-allowed
                          disabled:opacity-50
                        "
                      >
                        Cancel
                      </button>

                      <button
                        type="submit"
                        disabled={isSaving}
                        className="
                          inline-flex
                          h-[40px]
                          items-center
                          justify-center
                          gap-2
                          rounded-[10px]
                          px-5
                          text-[10px]
                          font-semibold
                          tracking-[0.02em]
                          text-white
                          shadow-[0_6px_18px_rgba(32,72,91,0.16)]
                          transition-all
                          duration-200
                          hover:shadow-[0_8px_22px_rgba(32,72,91,0.22)]
                          active:scale-[0.98]
                          disabled:cursor-not-allowed
                          disabled:opacity-60
                          disabled:shadow-none
                          border-[#4c7a2e]
                      bg-[#4c7a2e]
                    hover:-translate-y-px
                    hover:border-[#375c20]
                    hover:bg-[#375c20]
                    hover:shadow-[#375c20]
                        "
                      >
                        <Save size={13} strokeWidth={2} />

                        {isSaving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  {/* <div className="mt-5 flex justify-end gap-2 border-t border-[#eeeeeb] pt-4">
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={isSaving}
                      className="
                        rounded-lg
                        border
                        border-[#dfe3df]
                        bg-white
                        px-4
                        py-2.5
                        text-[9px]
                        font-semibold
                        text-[#667074]
                        transition
                        hover:bg-[#f7f7f5]
                      "
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={isSaving}
                      className="
                        flex
                        items-center
                        gap-1.5
                        rounded-lg
                        bg-[#20485b]
                        px-4
                        py-2.5
                        text-[9px]
                        font-semibold
                        text-white
                        transition
                        hover:bg-[#183b4a]
                        disabled:cursor-not-allowed
                        disabled:opacity-60
                      "
                    >
                      <Save size={12} />

                      {isSaving
                        ? "Saving..."
                        : "Save Changes"}
                    </button>
                  </div> */}
                </form>
              )}
            </>
          ) : (
            <div className="py-8 text-center">
              <User
                size={30}
                className="mx-auto text-[#20485b]"
              />

              <p className="mt-3 text-[11px] font-medium text-[#526067]">
                Unable to load profile information.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
